import {
  Avatar,
  CloseButton,
  CrudMode,
  DELETE_WARNING,
  DropDownMenu,
  ListContext,
  ListContextValue,
  MenuItemType,
  Stack,
  Typography,
  UpdateButton,
  ValidationErrors,
  newToast,
} from "@compass/core";
import { DataModelInformation, SubmitButton } from "components";
import { DataModelEnum, LkRoleId, Permission, PermissionAction, User } from "generated/openApi";
import { useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import { RootState } from "redux/store";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";

export const UserPageHeader = (props: {
  rootPermission: Permission;
  handle: string;
  crudMode: CrudMode;
  fetch: (userMembership: string) => void;
  reroute: (membershipId: string, crudMode: CrudMode) => void;
  user: User;
  appUserRoleId: number | undefined;
  userMembership: string;
  userPermissions: number[];
  setValidationErrors: (errors: ValidationErrors) => void;
  unsavedChanges: boolean;
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const listContext = useContext<ListContextValue>(ListContext);
  const id = props.user.id;
  const membership = props.userMembership;
  const loggedInUserId = useSelector((state: RootState) => state.app.userId);
  const avatarColor = useSelector((state: RootState) => state.app.avatarColor);

  // Gets the current logged in user's membership
  const appUserMembership: string | undefined = useSelector((state: RootState) => state.app.userMembership);

  const save = (user: User) => {
    props.setValidationErrors({});

    if (membership && membership != "0") {
      if (membership === appUserMembership) {
        ApiService.users
          .saveUpdateSelf(user)
          .then(() => {
            props.fetch(membership);
            listContext.loadList?.();
            dispatch(setAppToast(newToast("success")));
            props.reroute(membership, "view");
          })
          .catch((ex) => {
            dispatch(setAppToast(newToast("error")));
            props.setValidationErrors(ex.response);
          });
        if (props.appUserRoleId == LkRoleId.Cobro || props.appUserRoleId == LkRoleId.Administrator) {
          ApiService.users.savePermissions(props.userPermissions, props.user.id).then(() => {
          })
            .catch((ex) => {
              console.log("ex");
            });
        }
      }
      else {
        ApiService.users
          .saveUpdate(user)
          .then(() => {
            props.fetch(membership);
            listContext.loadList?.();
            dispatch(setAppToast(newToast("success")));
            props.reroute(membership, "view");
          })
          .catch((ex) => {
            dispatch(setAppToast(newToast("error")));
            props.setValidationErrors(ex.response);
          });
        ApiService.users.savePermissions(props.userPermissions, props.user.id).then(() => {

        })
          .catch((ex) => {
            console.log("ex");
          });
      }
    }
    else {
      ApiService.users
        .saveCreate(user)
        .then((res) => {
          const id = res;
          listContext.loadList?.();
          // send user invitation email
          ApiService.users.sendInvitationEmail(res).then((response) => {
            props.fetch(response);
            props.reroute(response, "view");
            dispatch(setAppToast(newToast("success")));
          });

          dispatch(setAppToast(newToast("success")));
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          props.setValidationErrors(ex.response);
        });
    };
  }

  const postDelete = (id: number) => {
    if (window.confirm(DELETE_WARNING)) {
      ApiService.users.delete(id).then(() => {
        navigate("../");
        listContext.loadList?.();
        dispatch(setAppToast(newToast("success")));
      });
    }
  };

  const deactivate = (id: number) => {
    if (window.confirm("Are you sure you want to deactivate this user?. They will no longer be able to login.")) {
      ApiService.users.deactivate(id).then(() => {
        props.fetch(membership);
        listContext.loadList?.();
        dispatch(setAppToast(newToast("success")));
      });
    }
  };

  const activate = (id: number) => {
    if (window.confirm("Are you sure you want to activate this user?")) {
      ApiService.users.activate(id).then(() => {
        props.fetch(membership);
        listContext.loadList?.();
        dispatch(setAppToast(newToast("success")));
      });
    }
  };

  const menuItems: MenuItemType[] = [];
  if (id > 0) {
    if (isAuthorized(props.rootPermission, PermissionAction.Delete) && !props.user.deactivated)
      menuItems.push({
        label: <Typography>Deactivate</Typography>,
        action: () => deactivate(id),
      });

    if (isAuthorized(props.rootPermission, PermissionAction.Update) && props.user.deactivated)
      menuItems.push({
        label: <Typography>Activate</Typography>,
        action: () => activate(id),
      });

    if (isAuthorized(props.rootPermission, PermissionAction.Delete))
      menuItems.push({
        label: <Typography color="error">Delete</Typography>,
        action: () => postDelete(id),
      });
  }

  return (
    <Stack direction="row" justifyContent="space-between">
      <Stack direction="row" alignItems="center" spacing="sm">
        <Avatar bgcolor={avatarColor} string={`${props.user.firstName} ${props.user.lastName}`} size="large" /> 

        <Stack>
          <Stack direction="row" spacing="sm" alignItems="center">
            <Typography variant="h6">
              {props.user.firstName} {props.user.lastName}
            </Typography>
          </Stack>

          <Stack direction="row" spacing="md">
            <Stack direction="row" spacing="sm">
              <AccountBalanceIcon fontSize="small" opacity={0.3} />
              <Typography variant="caption">
                {props.user.role?.name}
              </Typography>
            </Stack>
          </Stack>
        </Stack>

        {menuItems.length > 0 && <DropDownMenu menuItems={menuItems} />}
      </Stack>
      <Stack direction="row" spacing="sm">
        <Stack>
          {/* <Typography variant="inherit">
            {id === 0 ? "New" : ""} {props.handle}
          </Typography> */}
          {props.user.deactivated && (
            <Typography color="error" variant="caption">
              Deactivated
            </Typography>
          )}
        </Stack>
      </Stack>

      <Stack direction="row" alignItems="center" spacing="sm">
        {props.user.id > 0 && <DataModelInformation color="primary" dataModelType={DataModelEnum.User} dataModelId={props.user.id} />}
        {props.crudMode === "view" && (isAuthorized(Permission.User, PermissionAction.Update) ||
          loggedInUserId == props.user.id) && (
            <UpdateButton variant="contained" onClick={() => props.reroute?.(props.userMembership, "edit")} />
          )}
        <CloseButton confirm={props.unsavedChanges} />

        {props.crudMode === "edit" && (
          <SubmitButton onClick={() => save(props.user)} disabled={!props.unsavedChanges} />
        )}
      </Stack>
    </Stack>
  );
}
